import { inject } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore, QueryRoute, RouteStore } from 'bernie-core';
import { Context } from 'bernie-context';
import { ErrorBoundary, Layer } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { MultiItemStore } from 'stores/multi-item-store';

import { PackageType } from 'models/multi-item/multi-item-types';

import { classNames } from 'uitk-react-utils';
import { ESSAdapter, TypeaheadRenderType, TypeaheadToolbarVersion, UitkTypeahead } from 'uitk-react-typeahead';

import { EssClientLogger } from 'typeahead-client-logger';
import { FormFieldBinding } from './form-field-binding';
import { SearchFormFieldProps } from 'components/common/search-form';

export interface LocationProps extends LocalizedComponentProps, SearchFormFieldProps {
  analytics?: AnalyticsStore;
  context?: Context;
  routeStore?: RouteStore;
  essAnalyticsLogger?: EssClientLogger;
  multiItem?: MultiItemStore;
}

// fields to reset on location change
export const RESET_FIELDS = ['poi', 'hotelName', 'group', 'neighborhood'];

@withLocalization
@FormFieldBinding
@inject('analytics', 'context', 'routeStore', 'multiItem')
export class Location extends React.Component<LocationProps, {}> {
  private pristine = true;
  private fieldRef;
  private timestamp = this.getCurrentTimestampInSeconds();

  constructor(props) {
    super(props);

    this.dismissTypeahead = this.dismissTypeahead.bind(this);
    this.openTypeahead = this.openTypeahead.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.validate = this.validate.bind(this);
    this.formValidator = this.formValidator.bind(this);
    this.renderTypeaheadDialog = this.renderTypeaheadDialog.bind(this);
    this.fieldRef = React.createRef();
    props.registerValidator(this.formValidator);
  }

  // tslint:disable no-multiple-render-methods
  public renderTypeaheadDialog(children) {
    return (
      <QueryRoute query={{ pwaDialog: 'typeahead' }}>
        <Layer id="home-typeahead" onDismiss={this.dismissTypeahead}>
          {children}
        </Layer>
      </QueryRoute>
    );
  }

  public dismissTypeahead() {
    const { analytics, routeStore } = this.props;
    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent('DismissDestinationTypeahead', 'Destination Typeahead Modal Dismissed', null);
    }
    if (routeStore && typeof routeStore.goBack === 'function') {
      routeStore.goBack();
    }
    if (this.fieldRef && this.fieldRef.current && typeof this.fieldRef.current.focusTypeaheadInput === 'function') {
      this.fieldRef.current.focusTypeaheadInput();
    }
  }

  public openTypeahead() {
    const { analytics, routeStore } = this.props;

    if (routeStore && typeof routeStore.updateSearch === 'function') {
      routeStore.updateSearch({ pwaDialog: 'typeahead' });
    }

    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent(':ChangeDestination', 'User searching Destination', null);
    }
  }

  public handleUpdate() {
    return selection => {
      const { updateModel, l10n } = this.props;
      const { formatDate } = l10n;

      const { data, term } = selection;
      const userHistory = data && data.category === 'USERHISTORY';
      this.timestamp = this.getCurrentTimestampInSeconds();

      if (typeof updateModel === 'function') {
        this.pristine = false;
        updateModel({
          target: {
            name: 'destination',
            value: term,
            track: true,
            userHistory,
          },
        });
        updateModel({
          target: {
            name: 'latLong',
            value: data && data.location ? `${data.location.lat},${data.location.long}` : '',
          },
        });
        updateModel({
          target: {
            name: 'regionId',
            value: data && data.regionId ? data.regionId : '',
          },
        });
        updateModel({
          target: {
            name: 'selected',
            value: data && data.selected ? data.selected : '',
          },
        });
        if (data && data.checkindate && data.checkoutdate) {
          updateModel({
            target: {
              name: 'startDate',
              value: formatDate(new Date(data.checkindate), { raw: 'yyyy-MM-dd' }),
            },
          });
          updateModel({
            target: {
              name: 'endDate',
              value: formatDate(new Date(data.checkoutdate), { raw: 'yyyy-MM-dd' }),
            },
          });
        }

        RESET_FIELDS.forEach(name =>
          updateModel({
            target: {
              name,
              value: undefined,
            },
          })
        );
      }
    };
  }

  public render() {
    const { context, l10n, model, wrapperClassNames, multiItem } = this.props;
    const { formatText } = l10n;
    const destination = formatText('hotels.search.form.destination.label');
    const isMultiItem = multiItem && multiItem.isPackageShoppingPath();

    const defaultEssAdapterConfig = {
      client: 'Hotels.Search',
      dest: true,
      essLogger: this.props.essAnalyticsLogger,
      expuserid: context && context.user && context.user.expUserId,
      features: 'ta_hierarchy|postal_code|contextual_ta|google|consistent_display',
      guid: context && context.deviceId,
      inputId: 'hotels-destination',
      lob: 'HOTELS',
      locale: context && context.locale,
      maxResults: 10,
      personalize: true,
      regionType: 2047,
      siteId: context?.site?.id,
      lastSearchLatLong: model?.latLong,
      lastSearchElapseTime: this.timestamp ? this.getCurrentTimestampInSeconds() - this.timestamp : undefined,
    };

    const multiItemEssAdapterConfig = {
      ...defaultEssAdapterConfig,
      ...{
        client: 'Packages.Search',
        features: 'nearby_airport|ta_hierarchy|consistent_display',
        lob: 'PACKAGES',
        subLob: isMultiItem ? multiItem.multiItemContext.packageType : '',
        personalize: false,
        regionType: 991,
        packageType: isMultiItem ? multiItem.multiItemContext.packageType : '',
      },
    };

    const defaultTypeaheadProps = {
      allowExactMatch: true,
      label: destination,
      icon: 'place',
      invalid: this.validate() && this.validate().text,
      name: 'destination',
      onClick: this.openTypeahead,
      onDismiss: this.dismissTypeahead,
      renderDialog: this.renderTypeaheadDialog,
      renderType: TypeaheadRenderType.DIALOG,
      stid: 'hotels-destination',
      toolbarVersion: TypeaheadToolbarVersion.V1,
      value: model && model.destination,
      ref: this.fieldRef,
      onUpdate: this.handleUpdate(),
    };

    const isFlightInSearchForm = isMultiItem && multiItem.multiItemContext.packageType !== PackageType.HC;
    const itemClass = classNames({
      'uitk-cell all-y-gutter-two s-cell-1-1 m-cell-1-2 l-cell-1-3 xl-cell-1-3 location': isFlightInSearchForm,
      'uitk-cell all-y-gutter-two m-cell-1-1 l-cell-1-3 xl-cell-1-3 location': !isFlightInSearchForm,
    });

    const adapter = isMultiItem ? new ESSAdapter({ ...multiItemEssAdapterConfig }) : new ESSAdapter({ ...defaultEssAdapterConfig });

    return (
      <ErrorBoundary label="location.typeahead">
        <div className={wrapperClassNames || itemClass}>
          <UitkTypeahead
            {...defaultTypeaheadProps}
            adapter={adapter}
            toolbarVersion={TypeaheadToolbarVersion.V2}
            renderType={TypeaheadRenderType.ADAPTIVE}
          />
        </div>
      </ErrorBoundary>
    );
  }

  private getCurrentTimestampInSeconds() {
    return Math.floor(Date.now() / 1000);
  }

  private formValidator() {
    this.pristine = false;

    return this.validate();
  }

  private validate() {
    const { l10n, model } = this.props;
    const { formatText } = l10n;

    if (this.pristine) {
      return;
    }

    const destination = model && model.destination;
    const focus =
      this.fieldRef &&
      this.fieldRef.current &&
      typeof this.fieldRef.current.focusTypeaheadInput === 'function' &&
      this.fieldRef.current.focusTypeaheadInput;

    if (!destination) {
      return {
        text: formatText('hotels.search.validation.invalidDestination.text'),
        focus,
      };
    }
  }
}
